import client from "~/v2/api/client"
import { mapVaasCourse, mapVaasCourseList } from "~/v2/mappers/vaas-course"
import { normalizeApiPayload } from "~/v2/mappers/vaas-helpers"
import { mapVaasLearningCueList } from "~/v2/mappers/vaas-learning-cue"
import { mapVaasLesson } from "~/v2/mappers/vaas-lesson"
import type {
  RawLearningCue,
  VaasCourse,
  VaasId,
  VaasLesson,
} from "~/v2/types/vaas"

type ApiData = Record<string, unknown>

type SaveVaasCourseResponse = {
  data: {
    courseId: VaasId
    vaasId: VaasId
  }
  message: string
}

const listCourses = (config?: ApiData): Promise<VaasCourse[]> =>
  client
    .get("/courses", config)
    .then((response: any) => mapVaasCourseList(response.data?.data))

const getCourse = (id: VaasId | null): Promise<VaasCourse | null> =>
  client
    .get(`/courses/${id}`)
    .then((response: any) => mapVaasCourse(response.data?.data))

const createCourse = (data: ApiData): Promise<VaasCourse | null> =>
  client
    .post("/courses", data)
    .then((response: any) => mapVaasCourse(response.data?.data))

const updateCourse = (data: ApiData, id: VaasId): Promise<VaasCourse | null> =>
  client
    .put(`/courses/${id}`, data)
    .then((response: any) => mapVaasCourse(normalizeApiPayload(response.data)))

const saveVaasCourse = (id: VaasId): Promise<SaveVaasCourseResponse> =>
  client.post(`/courses/${id}/save-vaas`).then((response: any) => {
    if (response.status !== 200) {
      throw new Error(`Unexpected VaaS course save status: ${response.status}`)
    }

    return response.data
  })

const addLesson = (data: ApiData[], id: VaasId): Promise<any> =>
  client
    .post(`/course_lessons/${id}`, data)
    .then((response: any) => response.data)

const publishCourse = (id: VaasId): Promise<VaasCourse | null> =>
  client
    .post(`/publish_course/${id}`)
    .then((response: any) => mapVaasCourse(normalizeApiPayload(response.data)))

const unpublishCourse = (id: VaasId): Promise<VaasCourse | null> =>
  client
    .post(`/unpublish_course/${id}`)
    .then((response: any) => mapVaasCourse(normalizeApiPayload(response.data)))

const createCourseVersion = (
  data: ApiData,
  id?: VaasId,
): Promise<VaasCourse | null> =>
  client
    .post(`/course_version/${id}`, data)
    .then((response: any) => mapVaasCourse(normalizeApiPayload(response.data)))

const getLesson = (id: VaasId | null): Promise<VaasLesson | null> =>
  client
    .get(`/lessons/${id}`)
    .then((response: any) => mapVaasLesson(response.data?.data))

const createLesson = (data: ApiData): Promise<VaasLesson | null> =>
  client
    .post("/lessons", data)
    .then((response: any) => mapVaasLesson(response.data?.data))

const updateLesson = (data: ApiData, id: VaasId): Promise<VaasLesson | null> =>
  client
    .put(`/lessons/${id}`, data)
    .then((response: any) => mapVaasLesson(normalizeApiPayload(response.data)))

const publishLesson = (id: VaasId): Promise<VaasLesson | null> =>
  client
    .post(`/publish_lesson/${id}`)
    .then((response: any) => mapVaasLesson(normalizeApiPayload(response.data)))

const unpublishLesson = (id: VaasId): Promise<VaasLesson | null> =>
  client
    .post(`/unpublish_lesson/${id}`)
    .then((response: any) => mapVaasLesson(normalizeApiPayload(response.data)))

const createLessonVersion = (
  data: ApiData,
  id?: VaasId,
): Promise<VaasLesson | null> =>
  client
    .post(`/lesson_version/${id}`, data)
    .then((response: any) => mapVaasLesson(normalizeApiPayload(response.data)))

const getVideo = (id: VaasId): Promise<any> =>
  client.get(`/videos/watch/${id}`).then((response: any) => response.data)

const getVideoSrt = (id: VaasId): Promise<any> =>
  client.get(`/videos/srt/${id}`).then((response: any) => response.data?.data)

const deleteSrt = (id: VaasId): Promise<any> =>
  client.delete(`/videos/srt/${id}`).then((response: any) => response.data)

const getLearningCues = (id: VaasId): Promise<RawLearningCue[]> =>
  client
    .get(`/lessons/${id}/learning_cues`)
    .then((response: any) => mapVaasLearningCueList(response.data?.data))

const setLearningCues = (
  data: RawLearningCue[],
  id: VaasId,
): Promise<RawLearningCue[]> =>
  client
    .post(`/lesson_learning_cues/${id}`, data)
    .then((response: any) => mapVaasLearningCueList(response.data?.data))

export {
  addLesson,
  createCourse,
  createCourseVersion,
  createLesson,
  createLessonVersion,
  deleteSrt,
  getCourse,
  getLearningCues,
  getLesson,
  getVideo,
  getVideoSrt,
  listCourses,
  publishCourse,
  publishLesson,
  saveVaasCourse,
  setLearningCues,
  unpublishCourse,
  unpublishLesson,
  updateCourse,
  updateLesson,
}
